import Service, { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { trackedFunction } from 'reactiveweb/function';
import { query } from '@warp-drive/utilities/json-api';
import {
  answerServicerQuestionRequest,
  type ServicerAnswerResult,
} from 'tio-data/requests/servicer-answer';
import {
  answerLoanIdentityQuestionRequest,
  type LoanIdentityAnswerResult,
  type LoanIdentityDecision,
  type LoanIdentityOutcome,
} from 'tio-data/requests/loan-identity-answer';
import {
  getLoanVerificationRequest,
  parseLoanVerification,
  type LoanVerification,
} from 'tio-data/requests/loan-verification';
import { parseCommandResult } from 'tio-data/requests/command-result';
import type { Store } from '@warp-drive/core';
import type { FinancialInstitution as FinancialInstitutionModel } from 'tio-data/data/resources/financial-institution.type';
import {
  resolutionAlertKey,
  type ResolutionAlert,
  type ServicerOption,
} from 'tio-employee/utils/slr/resolution-alert';

/** What the member answered, kept for the page session so every surface shows it at once. */
export interface MemberAnswer {
  servicer: ServicerOption;
  /** False when the server had already applied this servicer — a replay. */
  applied: boolean;
}

/** A submitted loan verification, kept for the page session so every surface confirms it. */
export interface LoanVerificationAnswer {
  decisions: LoanIdentityOutcome[];
  /** False when every decision repeated an answer already recorded — a replay. */
  recorded: boolean;
  /** Epoch ms when the answer POST resolved; a feed requested later that still asks it means a re-ask. */
  recordedAt: number;
}

// The server caps a page at 400; the loop stops early on a short page.
const CATALOG_PAGE_SIZE = 400;
const CATALOG_MAX_PAGES = 5;

/**
 * Page-session state behind the member resolution-alert host: the answers given and the
 * temporary alerts dismissed since the app booted, plus the live servicer catalog the
 * picker ranks. Nothing here is persisted — a reload starts from the server feed alone.
 */
export default class MemberResolutionsService extends Service {
  @service declare store: Store;

  @tracked private answers: Record<string, MemberAnswer | LoanVerificationAnswer> = {};
  @tracked private dismissed: Record<string, true> = {};

  answerFor = (alert: Pick<ResolutionAlert, 'resolutionId' | 'documentId'>) =>
    this.answers[resolutionAlertKey(alert)];

  isDismissed = (alert: Pick<ResolutionAlert, 'resolutionId' | 'documentId'>) =>
    this.dismissed[resolutionAlertKey(alert)] === true;

  /** Whether the alert still asks something of the member on this page session. */
  isOpen = (alert: Pick<ResolutionAlert, 'resolutionId' | 'documentId'>) =>
    !this.answerFor(alert) && !this.isDismissed(alert);

  /** Hides the dashboard row for this page session; the question stays open everywhere else. */
  dismiss = (alert: Pick<ResolutionAlert, 'resolutionId' | 'documentId'>) => {
    this.dismissed = { ...this.dismissed, [resolutionAlertKey(alert)]: true };
  };

  /**
   * Records the member's pick for the question's document and remembers it for the page
   * session. Network call: POST /api/v2/resolutions/:id/answer.
   *
   * @throws the request error unchanged — status 409 when a different servicer is already
   *   applied, 404 when the question has closed or the upload no longer waits on it; nothing is
   *   remembered on a throw
   */
  answerServicer = async (alert: ResolutionAlert, servicer: ServicerOption) => {
    const { content } = await this.store.request(
      answerServicerQuestionRequest(alert.resolutionId, {
        documentId: alert.documentId,
        financialInstitutionId: servicer.id,
      })
    );
    const result = parseCommandResult<ServicerAnswerResult>(content);
    const answer: MemberAnswer = { servicer, applied: result.applied };
    this.answers = { ...this.answers, [resolutionAlertKey(alert)]: answer };
    return answer;
  };

  /**
   * Reads the "Verify Your Loans" page for the question a `loan-identity` alert opened.
   * Network call: GET /api/v2/resolutions/:id/loan-verification.
   *
   * @throws the request error unchanged — status 404 when the question is closed or not the
   *   caller's; a malformed body throws a plain Error
   */
  loanVerification = async (resolutionId: string) => {
    const { content } = await this.store.request(getLoanVerificationRequest(resolutionId));
    return parseLoanVerification(content);
  };

  /**
   * Submits one decision per held loan and, once recorded, remembers the answer under the
   * page's question and document — the key the alert that opened it carries. Network call:
   * POST /api/v2/resolutions/:id/answer.
   *
   * @throws the request error unchanged — 409 when the page is stale (read
   *   `readLoanIdentityRefusal` for `discardedHoldIds`), 404 when the question has closed, 422
   *   with a pointer per refused decision; nothing is remembered on a throw
   */
  answerLoanIdentity = async (
    verification: LoanVerification,
    decisions: LoanIdentityDecision[]
  ) => {
    const { content } = await this.store.request(
      answerLoanIdentityQuestionRequest(verification.resolutionId, decisions)
    );
    const result = parseCommandResult<LoanIdentityAnswerResult>(content);
    const answer: LoanVerificationAnswer = {
      decisions: result.decisions,
      recorded: result.recorded,
      recordedAt: Date.now(),
    };
    this.answers = { ...this.answers, [resolutionAlertKey(verification)]: answer };
    return answer;
  };

  /** Every active, public servicer, loaded once per page session; a failed load rejects, setting `isError`. */
  servicerCatalog = trackedFunction(this, async () => {
    const catalog: ServicerOption[] = [];
    try {
      for (let page = 0; page < CATALOG_MAX_PAGES; page++) {
        const { content } = (await this.store.request(
          query<FinancialInstitutionModel>('financial-institution', {
            'filter[isActive]': true,
            'filter[isPublic]': true,
            sort: 'name',
            'page[limit]': CATALOG_PAGE_SIZE,
            'page[offset]': page * CATALOG_PAGE_SIZE,
          })
        )) as { content: { data: FinancialInstitutionModel[] } };
        const rows = content.data ?? [];
        for (const institution of rows) {
          if (institution.id && institution.name) {
            catalog.push({ id: institution.id, name: institution.name });
          }
        }
        if (rows.length < CATALOG_PAGE_SIZE) break;
      }
      return catalog;
    } catch (error) {
      console.warn('[member-resolutions] Failed to load the servicer catalog', error);
      throw error;
    }
  });
}
